import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Award, Download, Linkedin, Share2, ArrowRight } from "lucide-react";

export default function VerifyCertificate() {
  const { number } = useParams();
  const [cert, setCert] = useState(null);

  useEffect(() => {
    api.get(`/certificates/${number}`).then((r) => setCert(r.data)).catch(() => setCert(false));
  }, [number]);

  if (cert === null) return <div className="py-32 text-center text-[var(--ink-2)]">Verifying…</div>;

  const pageUrl = typeof window !== "undefined" ? window.location.href : "";
  const dt = cert && cert.completion_date ? new Date(cert.completion_date) : null;
  const issueYear = dt ? dt.getFullYear() : "";
  const issueMonth = dt ? dt.getMonth() + 1 : "";

  const linkedInAddUrl = cert && `https://www.linkedin.com/profile/add?${new URLSearchParams({
    startTask: "CERTIFICATION_NAME",
    name: cert.course_title || "",
    organizationName: cert.provider_name || "Get Skilled in Days Not Years",
    issueYear: String(issueYear),
    issueMonth: String(issueMonth),
    certUrl: pageUrl,
    certId: cert.certificate_number || "",
  }).toString()}`;
  const shareTarget = `${API}/certificates/${number}/share`;
  const linkedInShareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareTarget)}`;

  const copyLink = async () => {
    try { await navigator.clipboard.writeText(pageUrl); toast.success("Certificate link copied"); }
    catch { toast.error("Could not copy link"); }
  };

  return (
    <div className="mx-auto max-w-xl px-4 py-16" data-testid="verify-certificate-page">
      {cert === false ? (
        <div className="border border-[var(--line)] bg-white p-10 text-center">
          <XCircle className="mx-auto text-[var(--destructive,#D50000)] mb-3" size={48} />
          <h1 className="font-display font-bold text-2xl mb-2">Certificate not found</h1>
          <p className="text-[var(--ink-2)] text-sm">No certificate matches reference <strong>{number}</strong>.</p>
        </div>
      ) : (
        <>
          <div className="border border-[var(--ink)] bg-white" data-testid="certificate-valid">
            <div className="bg-[var(--ink)] text-white p-6 flex items-center gap-3">
              <img src="/logo.png" alt="" className="h-9 w-9 object-contain" />
              <div><div className="font-display font-extrabold text-sm tracking-tight">GET SKILLED IN DAYS NOT YEARS</div><div className="text-xs text-white/60">Certificate verification</div></div>
            </div>
            <div className="p-8 text-center">
              <CheckCircle2 className="mx-auto text-[#1B5E20] mb-3" size={48} />
              <p className="text-sm text-[#1B5E20] font-semibold mb-6">This is a valid, verified certificate.</p>
              <Award className="mx-auto text-[var(--accent)] mb-3" size={32} />
              <h1 className="font-display font-extrabold text-2xl">{cert.learner_name}</h1>
              <p className="text-[var(--ink-2)] mt-1">successfully completed</p>
              <p className="font-display font-bold text-lg mt-1">{cert.course_title}</p>
              <div className="mt-6 grid grid-cols-2 gap-3 text-sm text-left border-t border-[var(--line)] pt-6">
                <div><div className="text-xs text-[var(--ink-2)] uppercase">Certificate no</div><div className="font-semibold">{cert.certificate_number}</div></div>
                <div><div className="text-xs text-[var(--ink-2)] uppercase">Completed</div><div className="font-semibold">{cert.completion_date}</div></div>
                <div><div className="text-xs text-[var(--ink-2)] uppercase">Provider</div><div className="font-semibold">{cert.provider_name}</div></div>
                {cert.cpd_hours ? <div><div className="text-xs text-[var(--ink-2)] uppercase">CPD hours</div><div className="font-semibold">{cert.cpd_hours}</div></div> : null}
              </div>

              <div className="mt-7 flex flex-col sm:flex-row flex-wrap justify-center gap-3">
                <a href={linkedInAddUrl} target="_blank" rel="noreferrer" data-testid="add-to-linkedin" className="inline-flex items-center justify-center gap-2 bg-[#0A66C2] text-white font-semibold px-5 py-3 hover:bg-[#084e96] transition-colors"><Linkedin size={16} /> Add to LinkedIn</a>
                <a href={linkedInShareUrl} target="_blank" rel="noreferrer" data-testid="share-linkedin" className="inline-flex items-center justify-center gap-2 border border-[var(--ink)] font-semibold px-5 py-3 hover:bg-[var(--ink)] hover:text-white transition-colors"><Share2 size={16} /> Share</a>
                <button onClick={copyLink} data-testid="copy-cert-link" className="inline-flex items-center justify-center gap-2 border border-[var(--line)] font-semibold px-5 py-3 hover:border-[var(--ink)] transition-colors">Copy link</button>
              </div>
              <a href={`${API}/certificates/${cert.certificate_number}/pdf`} target="_blank" rel="noreferrer" data-testid="verify-download" className="mt-4 inline-flex items-center gap-2 text-sm text-[var(--ink-2)] hover:text-[var(--ink)]"><Download size={15} /> Download PDF</a>
            </div>
          </div>

          {/* Referral CTA */}
          <div className="mt-6 border border-[var(--accent)] bg-white p-6 text-center" data-testid="cert-referral-cta">
            <p className="font-display font-bold text-lg">Want a certificate like this?</p>
            <p className="text-sm text-[var(--ink-2)] mt-1 mb-4">Get skilled in days, not years — short, career-focused courses you can finish from your phone.</p>
            <Link to="/courses" data-testid="cert-browse-courses" className="inline-flex items-center gap-2 bg-[var(--accent)] text-white font-semibold px-6 py-3 hover:bg-[var(--accent-hover)]">Browse courses <ArrowRight size={16} /></Link>
          </div>
        </>
      )}
      <p className="mt-6 text-center text-sm"><Link to="/" className="text-[var(--ink-2)] hover:text-[var(--ink)]">← Back to Get Skilled</Link></p>
    </div>
  );
}
